import * as Dialog from '@radix-ui/react-dialog';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import { formatPrice, getPartPrefix } from '@/components/shared/utility/format';
import { BATTERY_TIRE } from '@/const/part-item';
import { GENUINE_PARTS } from '@/contents/lcv/parts';
import { keyframes, styled } from '@/stitches.config';
import { IGenuinePart, IGenuinePartInfo } from '@/types/shared/parts';

const GenuinePartsLogo = styled(Box, {
  height: 28,
  '& > *': {
    maxHeight: '100%',
    maxWidth: '$size-32',
  },
  variants: {
    type: {
      'isuzu-logo': {
        width: 'calc(72px + (64 - 72) * ((100vw - 360px) / (1440 - 360)))',
      },
      'tripetch-logo': {
        width: 'calc(120px + (120 - 124) * ((100vw - 360px) / (1440 - 360)))',
      },
      'bvp-logo': {
        width: 'calc(72px + (64 - 72) * ((100vw - 360px) / (1440 - 360)))',
      },
    },
  },
  defaultVariants: {
    type: 'isuzu-logo',
  },
});

const PartInfoCardWrap = styled(Box, {
  width: 'calc(100% - 32px)',
  maxWidth: 312,
});

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
});

const DialogOverlay = styled(Dialog.Overlay, {
  position: 'fixed',
  width: 'calc(100% + 1px)',
  height: 'calc(var(--viewport-height) + 1px)',
  top: -1,
  left: -1,
  bc: '$backdrop70',
  zIndex: '1001',
  cursor: 'pointer',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${overlayShow}`,
    animationDuration: '1s',
    animationTimingFunction: 'var(--transition-easing)',
  },
  '@coarse': {
    border: '1px solid $gray300',
  },
});

const DialogContent = styled(Dialog.Content, {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'max-content',
  zIndex: '1002',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${contentShow}`,
    animationDuration: 'var(--transition-duration)',
    animationTimingFunction: 'var(--transition-easing)',
  },
  '&:focus': { outline: 'none' },
});

const ImageTrigger = styled(Box, {
  position: 'relative',
  all: 'unset',
  cursor: 'pointer',
  br: '100%',
  display: 'inline-block',
  padding: '$space-1 $space-2',
});

interface PartInfo {
  title?: string;
  description?: string;
  dataTrack?: string;
  dataTrackValue?: string;
  dataTest?: string;
  dataTestSection?: string;
  open?: boolean;
  setOpenDialog?: (val: boolean) => void;
  children?: ReactNode;
}

const PartInfoCard = ({ title, description, setOpenDialog }: PartInfo) => {
  return (
    <PartInfoCardWrap
      position="relative"
      padding="4"
      margin="auto"
      boxShadow="300"
      rounded="2"
      backgroundColor="white"
    >
      <Text
        font="bold"
        dangerouslySetInnerHTML={{ __html: title as string }}
        css={{ marginBottom: '$space-3' }}
      />
      <Text
        dangerouslySetInnerHTML={{ __html: description as string }}
        css={{ whiteSpace: 'pre-wrap' }}
      />
      <ButtonIcon
        onClick={() => setOpenDialog?.(false)}
        icon="close"
        variant="button-icon-color-gray-dark"
        size="md"
        isTransparent
        css={{
          position: 'absolute',
          right: '$space-1',
          top: '$space-1',
        }}
      />
    </PartInfoCardWrap>
  );
};

const PartInfoDialog = ({
  title,
  description,
  dataTest,
  dataTrack,
  dataTrackValue,
  open,
  setOpenDialog,
}: PartInfo) => {
  return (
    <>
      <Dialog.Root open={open} onOpenChange={(open) => setOpenDialog?.(open)}>
        <Dialog.Trigger
          asChild
          data-test={`${dataTest}_dialog_trigger`}
          data-track={`${dataTrack}`}
          data-track-section={`${dataTest}`}
          data-track-text={`${title}`}
          data-track-value={`${dataTrackValue}`}
        >
          <ImageTrigger as="button" role="button" aria-label="button">
            <Icon icon="info" size="xs" color="icon-blue" />
          </ImageTrigger>
        </Dialog.Trigger>
        <Dialog.Portal>
          <DialogOverlay data-test={`${dataTest}_dialog_overlay`} />
          <DialogContent data-test={`${dataTest}_dialog_content`}>
            <PartInfoCard
              title={title}
              description={description}
              setOpenDialog={setOpenDialog}
            />
          </DialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

const GenuinePartItem = ({
  genuinePart,
  index,
  dataTest,
  dataTrack,
}: GenuinePartTableProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const setOpenDialog = useCallback(
    (val) => {
      setOpen(val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, setOpen]
  );
  const LOGO = useMemo<IGenuinePartInfo>(
    () =>
      genuinePart?.isTripetchGenuinePart
        ? GENUINE_PARTS.tripetchGenuinePart
        : genuinePart?.isBvpPart
          ? GENUINE_PARTS.bvpGenuinePart
          : GENUINE_PARTS.isuzuGenuinePart,
    [genuinePart]
  );

  return (
    <Box
      position="relative"
      backgroundColor="gray200"
      border="1"
      borderColor="gray300"
      rounded="4"
      overflow="hidden"
      display="flex"
      direction={{ '@initial': 'row', '@xl': 'column' }}
      justifyContent="center"
      alignItems="center"
      gap="4"
      css={{
        padding: '$space-3 $space-6',
        flex: 1,
        '@xl': {
          padding: '$space-4',
        },
        '@2xl': {
          padding: '$space-5',
        },
      }}
      data-test={`${dataTest}_part_${index}`}
    >
      {!!genuinePart && (
        <Flex alignItems="center" justifyContent="center" css={{ flex: 1 }}>
          <GenuinePartsLogo
            type={
              genuinePart.isIsuzuGenuinePart
                ? 'isuzu-logo'
                : genuinePart.isBvpPart
                  ? 'bvp-logo'
                  : 'tripetch-logo'
            }
            test={{
              dataTest: `${dataTest}_part_${index}_logo`,
            }}
          >
            <Image
              src={LOGO.src}
              alt={LOGO.alt}
              width={LOGO.dimension.width}
              height={LOGO.dimension.height}
              layout="responsive"
              useSrcSetPattern
            />
          </GenuinePartsLogo>
        </Flex>
      )}
      <Flex
        direction="column"
        justifyContent="center"
        alignItems={{ '@initial': 'start', '@xl': 'center' }}
        css={{
          flex: 1,
          justifyItems: 'flex-end',
        }}
      >
        <Text
          variant="text-gray-darkest"
          size="text-base"
          font="bold"
          wrap
          css={{ '@maxlg': { textAlign: 'center' } }}
          dangerouslySetInnerHTML={{
            __html: `${genuinePart ? getPartPrefix(genuinePart) : ''}`,
          }}
          data-test={`${dataTest}_part_${index}_title`}
        />
        <Text
          variant="text-red"
          size={{
            '@initial': 'text-base-2',
            '@media(min-width: 360px)': 'text-base',
          }}
          font="bold"
          dangerouslySetInnerHTML={{
            __html: `${genuinePart?.isStartPrice ? 'เริ่ม' : ' '} ${formatPrice(genuinePart?.price ?? '', false)} ${genuinePart?.unit}`,
          }}
          data-test={`${dataTest}_part_${index}_price_unit`}
        />
      </Flex>

      <Box
        css={{
          position: 'absolute',
          top: 0,
          right: 0,
        }}
      >
        <PartInfoDialog
          title={LOGO.title}
          description={LOGO.description}
          open={open}
          setOpenDialog={setOpenDialog}
          dataTrack={dataTrack}
          dataTrackValue={LOGO.dataTrackValue}
          dataTest={dataTest}
        />
      </Box>
    </Box>
  );
};

const PartItem = ({
  categoryUid,
  genuinePart,
  index,
  dataTest,
}: GenuinePartTableProps) => {
  if (!genuinePart) return null;

  return (
    <Box
      key={index}
      display="flex"
      justifyContent={
        !genuinePart?.isStartPrice &&
        getPartPrefix(genuinePart as IGenuinePart).length > 0
          ? 'between'
          : 'center'
      }
      test={{
        dataTest: `${dataTest}_part_${index}`,
      }}
    >
      {!genuinePart?.isStartPrice && (
        <Text
          wrap
          dangerouslySetInnerHTML={{
            __html: getPartPrefix(genuinePart),
          }}
          data-test={`${dataTest}_part_${index}_title`}
        />
      )}
      <Text
        variant="text-red"
        font="bold"
        wrap
        dangerouslySetInnerHTML={{
          __html: `${genuinePart.isStartPrice ? 'เริ่ม' : ''} ${
            categoryUid === BATTERY_TIRE &&
            parseFloat(genuinePart.specialPrice) > 0
              ? formatPrice(genuinePart.specialPrice, false)
              : formatPrice(genuinePart.price, false)
          } ${genuinePart.unit}`,
        }}
        data-test={`${dataTest}_part_${index}_price_unit`}
      />
    </Box>
  );
};

interface GenuinePartTableProps {
  genuineParts?: IGenuinePart[];
  categoryUid?: string;
  genuinePart?: IGenuinePart;
  index?: number;
  dataTrack?: string;
  dataTrackValue?: string;
  dataTest?: string;
  dataTestSection?: string;
}

export const GenuinePartTable = ({
  categoryUid,
  genuineParts,
  dataTest,
  dataTestSection,
  dataTrack = 'part-details',
}: GenuinePartTableProps) => {
  const isGenuinePart =
    genuineParts &&
    genuineParts.length > 0 &&
    genuineParts.filter(
      (p) => p.isIsuzuGenuinePart || p.isTripetchGenuinePart || p.isBvpPart
    ).length > 0;

  return (
    <>
      {isGenuinePart ? (
        <Box
          display="flex"
          direction={{
            '@initial': 'column',
            '@xl': 'row',
          }}
          justifyContent="evenly"
          alignItems="stretch"
          gap={{ '@initial': '2', '@md': '3' }}
          test={{
            dataTest: `${dataTest}_genuine_table`,
          }}
        >
          {genuineParts.map((o, i) => (
            <GenuinePartItem
              key={i}
              index={i}
              data-test={`${dataTest}_part_${i}`}
              genuinePart={o}
              dataTest={dataTest}
              dataTestSection={dataTestSection}
              dataTrack={dataTrack}
              genuineParts={[]}
            />
          ))}
        </Box>
      ) : (
        <Box
          backgroundColor="primary300"
          padding="4"
          display="flex"
          direction="column"
          data-test={`${dataTest}_part_table`}
        >
          {!!genuineParts &&
            genuineParts.map((o, i) => (
              <PartItem
                key={i}
                index={i}
                categoryUid={categoryUid}
                genuinePart={o}
                dataTest={dataTest}
                dataTestSection={dataTestSection}
                dataTrack={dataTrack}
                genuineParts={[]}
              />
            ))}
        </Box>
      )}
    </>
  );
};
GenuinePartTable.displayName = 'GenuinePartTable';
